import { useEffect } from 'react';
import type { FC } from 'react';
import { memo } from 'react';
import type { CreativesViewProps } from '../types';
import Masonry from '@mui/lab/Masonry';
import { Box } from '@mui/material';
import { useCreatives } from '../api';
import CreativeItem from './CreativeItem';
import { isEqual, sortBy } from 'lodash-es';
import CreativeEmptyView from './CreativeEmptyView';
import useInitMasonryColumns from '../../../hooks/useInitMasonryColumns';

const CreativesView: FC<CreativesViewProps> = ({
  limit,
  offset,
  orderBy,
  desc,
  domains,
  flows,
  adType,
  channels,
  period,
  tags,
  firstSeen,
  apps,
  languages,
  setTotal,
  reset,
}) => {
  const {
    data: { data: creatives, count },
  } = useCreatives({
    params: {
      limit,
      offset,
      order_by: orderBy,
      desc,
      domain_id: domains,
      flow_id: flows,
      ad_type: adType,
      channel: channels,
      period,
      tag_id: tags,
      first_seen: firstSeen,
      app_id: apps,
      language: languages,
    },
  });

  useEffect(() => {
    setTotal(count);
  }, [setTotal, count]);

  const { columns } = useInitMasonryColumns();

  useEffect(() => {
    const creativePageContainer = document.querySelector('#creatives-page');
    if (creativePageContainer) creativePageContainer.scrollTop = 0;
  }, [offset, limit]);

  return (
    <>
      <Box sx={{ display: 'flex', width: 'calc(100% + 30px)', mx: '-15px' }}>
        {creatives.length ? (
          <Masonry columns={columns} spacing={3} sx={{ '&': { m: 0 } }}>
            {creatives.map((creativeEntry) => (
              <CreativeItem key={creativeEntry.creative_id} creativeEntry={creativeEntry} />
            ))}
          </Masonry>
        ) : (
          <CreativeEmptyView reset={reset} />
        )}
      </Box>
    </>
  );
};

export default memo(CreativesView, (prevProps, nextProps) => {
  const res = Object.entries(nextProps).reduce((resulting, [key, value]) => {
    const prop = key as keyof CreativesViewProps;
    if (Array.isArray(value)) {
      const prevValue = prevProps[prop] as typeof value;
      return resulting && isEqual(sortBy(prevValue), sortBy(value));
    }
    return resulting && prevProps[prop] === value;
  }, true);
  return res;
});
